import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXS } from '@sorare/core/src/atoms/typography';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { Color } from '@sorare/core/src/style/types';

import type { StreaksPromotion_anyCardInterface } from './__generated__/index.graphql';
import bg from './assets/bg.jpg';

const Wrapper = styled(Horizontal).attrs({ gap: 2 })`
  overflow: hidden;
  position: relative;

  text-align: left;
  padding: var(--unit) var(--double-unit);
  border: 1px solid transparent;
  margin: 0 -1px;
  ${BrandXS} {
    --typography-font: var(--t-brand-xxs);
  }
  ${BrandM} {
    --typography-font: var(--t-brand-s);
  }

  @media (${tabletAndAbove}) {
    border-radius: var(--double-unit);
    padding: var(--double-unit);
    margin: 0;
    gap: var(--quadruple-unit);
    ${BrandXS} {
      --typography-font: var(--t-brand-xs);
    }
    ${BrandM} {
      --typography-font: var(--t-brand-m);
    }
  }
  background:
    linear-gradient(to right, var(--c-nl-800), var(--c-nl-400)) padding-box,
    no-repeat center / cover url(${bg}) padding-box,
    linear-gradient(160deg, #ff4e22, #fef314) border-box;
  box-shadow:
    0px 0px 4px 0px rgba(254, 193, 24, 0.25) inset,
    0px 0px 8px 0px rgba(255, 78, 34, 0.5);
`;

const Image = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
`;

type Props = {
  card: StreaksPromotion_anyCardInterface;
};

export const StreaksPromotion = ({ card }: Props) => {
  const streakTask = card.eligibleUpcomingLeagueTracks.find(
    leagueTrack => leagueTrack.myThresholdsStreakTask
  )?.myThresholdsStreakTask;

  if (!streakTask) {
    return null;
  }
  return (
    <Wrapper>
      <Vertical>
        <BrandXS color="var(--c-yellow-400)">
          <FormattedMessage
            id="StreaksPromotion.specialBoost"
            defaultMessage="Special Boost"
          />
        </BrandXS>
        <BrandM>
          <FormattedMessage
            id="StreaksPromotion.hotStreaks"
            defaultMessage="Hot streaks"
          />
        </BrandM>
      </Vertical>
      <Vertical>
        {!!streakTask.rewardTaskCampaign && (
          <Horizontal>
            <Image
              // Hack to use the homepage task icon
              src={streakTask.rewardTaskCampaign.tagIconUrl.replace(
                /task_campaigns\/campaigns/,
                'task_campaigns/homepage/campaigns'
              )}
              alt=""
            />
            <BrandXS color={streakTask.rewardTaskCampaign.color as Color}>
              {streakTask.rewardTaskCampaign.name}
            </BrandXS>
          </Horizontal>
        )}
        {!!streakTask.scoreTaskCampaign && (
          <Horizontal>
            <Image
              // Hack to use the homepage task icon
              src={streakTask.scoreTaskCampaign.tagIconUrl.replace(
                /task_campaigns\/campaigns/,
                'task_campaigns/homepage/campaigns'
              )}
              alt=""
            />
            <BrandXS color={streakTask.scoreTaskCampaign.color as Color}>
              {streakTask.scoreTaskCampaign.name}
            </BrandXS>
          </Horizontal>
        )}
      </Vertical>
      <Vertical />
    </Wrapper>
  );
};
StreaksPromotion.fragments = {
  anyCardInterface: gql`
    fragment StreaksPromotion_anyCardInterface on AnyCardInterface {
      slug
      eligibleUpcomingLeagueTracks {
        slug

        myThresholdsStreakTask {
          id
          rewardTaskCampaign {
            slug
            name
            tagIconUrl
            tagLabel
            color(variant: SECONDARY)
          }
          scoreTaskCampaign {
            slug
            name
            tagIconUrl
            tagLabel
            color(variant: SECONDARY)
          }
        }
      }
    }
  ` as TypedDocumentNode<StreaksPromotion_anyCardInterface>,
};
